import React, { Component } from "react";
import { AppBadge, AppCard, DataTableToolbar, PageLayout } from "../../components/ui";
import { OperatorDataModel } from "../../models/User";
import { Column } from "primereact/column";
import history from "../../history";
import { operatorsApiService } from "../../services/api/users/OperatorApiService";
import { toastService } from "../../services/toastService";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { rolesApiService } from "../../services/api/users/RolesApiService";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { numberFilterMatchModeOptions, stringFilterMatchModeOptions } from "../Paginator";
import { Button } from "primereact/button";
import { Plus, Users } from "lucide-react";

// TNPDEVAPPS-1812: "10n Admin" is managed only via the cross-merchant checkbox and must never be shown
// as an operator role (kept out of the User Administration grid's Roles column).
const HIDDEN_OPERATOR_ROLE = "10n Admin";

interface UsersAdministrationState {
    users: OperatorDataModel[];
    isLoading: boolean;
    totalRecords: number;
}

export default class UsersAdministration extends Component<Record<string, never>, UsersAdministrationState> {
    userStatusClassesMap: Map<string, string> = new Map<string, string>([
        ["Active", "success"],
        ["Inactive", "warning"],
        ["Deleted", "danger"],
    ]);
    statusTypes: Map<number, string> = new Map<number, string>();
    rolesDictionary: Map<number, string> = new Map<number, string>();
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: Record<string, never> | Readonly<Record<string, never>>) {
        super(props);

        this.state = {
            users: [],
            isLoading: false,
            totalRecords: 0,
        };
    }

    loadUsersData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });

        const statusTypes = await operatorsApiService.getStatusTypes();

        if (!statusTypes.success) {
            this.setState({ isLoading: false });
            toastService.error(statusTypes.message);
            return;
        }

        statusTypes.data.forEach((item) => {
            this.statusTypes.set(item.value, item.label);
        });

        const roleResult = await rolesApiService.getAll();

        if (!roleResult.success) {
            this.setState({ isLoading: false });
            toastService.error(roleResult.message);
            return;
        }
        roleResult.data.forEach((item) => {
            this.rolesDictionary.set(item.id, item.name);
        });

        const usersResult = await operatorsApiService.getAll(searchRequest);

        if (!usersResult.success) {
            this.setState({ isLoading: false });
            toastService.error(usersResult.message);
            return;
        }

        this.setState({ users: usersResult.data, totalRecords: usersResult.totalCount, isLoading: false });
    };

    onRowClick = (event: any) => {
        const user = event.data as OperatorDataModel;
        history.push(`/profile/${user.id}`);
    };

    renderStatusBadge(success: number, title: string) {
        const item = this.statusTypes.get(success);
        const className = this.userStatusClassesMap.get(item ?? "") ?? "danger";
        return <AppBadge variant={className as any}>{title}</AppBadge>;
    }

    renderRolesBadge(titles: string[]) {
        const className = "info";
        return (
            <div>
                {titles.map((title, idx) => (
                    <span key={idx} className="m-1"><AppBadge variant={className as any}>
                        {title}
                    </AppBadge></span>
                ))}
            </div>
        );
    }

    renderHeader = (hasActiveFilters: boolean) => <DataTableToolbar onClear={() => this.dataTable!.reset()} hasActiveFilters={hasActiveFilters} />;

    addUser = () => {
        history.push("/profile/create");
    };

    render() {
        return (
            <>
                <PageLayout heading="User Administration" icon={Users} actions={<Button label="Create User" icon={<Plus size={16} />} onClick={this.addUser} />}>
                    <AppCard className="mb-3">
                        <AppCard.Body className="!p-0">
                            <ExtendedDataTable
                                ref={(ref) => (this.dataTable = ref)}
                                header={this.renderHeader}
                                loading={this.state.isLoading}
                                value={this.state.users}
                                onChange={this.loadUsersData}
                                totalRecords={this.state.totalRecords}
                                defaultSortOrder={SortOrderType.Descending}
                                defaultSortField="lastLogin"
                                onRowClick={this.onRowClick}
                            >
                                <Column
                                    {...({ alwaysVisible: true } as any)}
                                    field="firstName"
                                    header="First Name"
                                    filterMatchModeOptions={stringFilterMatchModeOptions}
                                ></Column>
                                <Column
                                    {...({ alwaysVisible: true } as any)}
                                    field="lastName"
                                    header="Last Name"
                                    filterMatchModeOptions={stringFilterMatchModeOptions}
                                ></Column>
                                <Column
                                    {...({ alwaysVisible: true } as any)}
                                    header="Roles"
                                    body={(rowData: OperatorDataModel) =>
                                        this.renderRolesBadge(
                                            rowData.userRoles
                                                .map((item) => this.rolesDictionary.get(item)!)
                                                .filter((name) => name !== HIDDEN_OPERATOR_ROLE),
                                        )
                                    }
                                ></Column>
                                <Column
                                    field="merchantName"
                                    header="Merchant"
                                    filterMatchModeOptions={stringFilterMatchModeOptions}
                                ></Column>
                                <Column field="email" header="Email" filterMatchModeOptions={stringFilterMatchModeOptions}></Column>
                                <Column
                                    field="lastLogin"
                                    header="Last Login"
                                    body={dateBodyTemplate("lastLogin")}
                                    filterMatchModeOptions={numberFilterMatchModeOptions}
                                ></Column>
                                <Column
                                    field="status"
                                    header="Status"
                                    filterMatchModeOptions={numberFilterMatchModeOptions}
                                    body={(rowData: OperatorDataModel) =>
                                        this.renderStatusBadge(rowData.status, this.statusTypes.get(rowData.status)!)
                                    }
                                ></Column>
                            </ExtendedDataTable>
                        </AppCard.Body>
                    </AppCard>
                </PageLayout>
            </>
        );
    }
}
